import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";
import { useContent } from "../../../context/ContentContext";

const QuickAccessItem = ({ item }) => {
	const theme = useTheme();
	const styles = useStyles();

	return (
		<TouchableOpacity style={styles.quickAccessItem}>
			<View style={styles.iconContainer}>
				<Ionicons name={item.icon} size={20} color={theme.colors.primary} />
			</View>
			<Text style={styles.itemTitle} numberOfLines={1}>
				{item.label || item.title}
			</Text>
			{item.badge && (
				<View style={styles.badge}>
					<Text style={styles.badgeText}>{item.badge}</Text>
				</View>
			)}
		</TouchableOpacity>
	);
};

export const QuickAccess = ({ content, label = "Quick Access" }) => {
	const theme = useTheme();
	const styles = useStyles();
	const { fetchContent } = useContent();

	// Get default items from ContentContext
	const defaultItems = fetchContent("quickAccess") || {};

	// Format items for display
	const quickAccessItems = [];

	// Add enabled items from default context
	if (defaultItems.posts && defaultItems.posts.enabled) {
		quickAccessItems.push({
			id: "posts",
			...defaultItems.posts,
		});
	}

	if (defaultItems.featuredAssets && defaultItems.featuredAssets.enabled) {
		quickAccessItems.push({
			id: "featuredAssets",
			...defaultItems.featuredAssets,
		});
	}

	if (defaultItems.createAssets && defaultItems.createAssets.enabled) {
		quickAccessItems.push({
			id: "createAssets",
			...defaultItems.createAssets,
		});
	}

	// If items were provided directly, use those instead
	const displayItems = content && content.items && content.items.length > 0 ? content.items : quickAccessItems;

	if (!displayItems || displayItems.length === 0) {
		return null;
	}

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<Text style={styles.label}>{label}</Text>
			</View>
			<View style={styles.grid}>
				{displayItems.map((item) => (
					<QuickAccessItem key={item.id} item={item} />
				))}
			</View>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			backgroundColor: theme.colors.systemBackground.primary,
			marginBottom: theme.spacing.sm,
			borderRadius: theme.borderRadius.lg,
			...theme.shadows.sm,
		},
		header: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			paddingHorizontal: theme.spacing.md,
			paddingTop: theme.spacing.sm,
			paddingBottom: theme.spacing.xs,
		},
		label: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			fontWeight: "500",
		},
		grid: {
			flexDirection: "row",
			flexWrap: "wrap",
			paddingHorizontal: theme.spacing.sm,
			paddingBottom: theme.spacing.sm,
			gap: theme.spacing.xs,
		},
		quickAccessItem: {
			width: "calc(50% - 4px)",
			backgroundColor: theme.colors.systemBackground.secondary,
			borderRadius: theme.borderRadius.md,
			padding: theme.spacing.xs,
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
		},
		iconContainer: {
			width: 24,
			height: 24,
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.sm,
			justifyContent: "center",
			alignItems: "center",
		},
		itemTitle: {
			flex: 1,
			...theme.typography.footnote,
			color: theme.colors.text,
		},
		badge: {
			backgroundColor: theme.colors.error,
			borderRadius: theme.borderRadius.pill,
			paddingHorizontal: 6,
			paddingVertical: 2,
			minWidth: 18,
			alignItems: "center",
		},
		badgeText: {
			...theme.typography.caption2,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
	});
};
